import { IColumn } from '@fluentui/react';
import { IPLink, IPSelectionMode, IPStack, IPText } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import BaseTableList from 'components/TableList/BaseTableList';
import { StateIcon, StateIconSize, StateIconState } from 'components/Utils/IconsAndImages/StateIcon';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum CopilotApprovalOperationOutcome {
  Ok = 'Ok',
  Warning = 'Warning',
  NotFound = 'NotFound',
  Error = 'Error',
}

export interface CopilotApprovalOperation {
  toolName: string;
  arguments: string;
  message: string;
  entityCode: string;
  entityName: string;
  targetId: string;
  outcome: CopilotApprovalOperationOutcome | undefined;
}

interface ICopilotApprovalDetailsModalProps {
  isOpen: boolean;
  operations: CopilotApprovalOperation[];
  onClose: () => void;
}

type CopilotApprovalOperationItem = CopilotApprovalOperation & { id: string };

const getOutcomeState = (outcome: CopilotApprovalOperationOutcome | undefined): StateIconState | undefined => {
  switch (outcome) {
    case CopilotApprovalOperationOutcome.Ok:
      return StateIconState.Success;
    case CopilotApprovalOperationOutcome.Warning:
      return StateIconState.Warning;
    case CopilotApprovalOperationOutcome.NotFound:
    case CopilotApprovalOperationOutcome.Error:
      return StateIconState.Error;
    default:
      return undefined;
  }
};

const getEntityText = (item: CopilotApprovalOperationItem): string => {
  const entity = [item.entityCode, item.entityName].filter(Boolean).join(' - ');

  return entity || item.targetId;
};

export const CopilotApprovalDetailsModal = (props: ICopilotApprovalDetailsModalProps) => {
  const { t } = useTranslation(['copilot']);

  const getColumns = (): IColumn[] => [
    {
      key: 'status',
      name: t('copilot:Panel.ApprovalDetails.StatusColumn'),
      minWidth: 56,
      maxWidth: 56,
    },
    {
      key: 'message',
      name: t('copilot:Panel.ApprovalDetails.MessageColumn'),
      minWidth: 180,
      isResizable: true,
      isMultiline: true,
    },
    {
      key: 'arguments',
      name: t('copilot:AgentEvents.Meta.Arguments'),
      minWidth: 200,
      isResizable: true,
      isMultiline: true,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(getColumns());
  const [showArgumentsColumn, setShowArgumentsColumn] = useState<boolean>(false);

  const items: CopilotApprovalOperationItem[] = props.operations.map((op, idx) => ({ ...op, id: String(idx) }));
  const visibleColumns = showArgumentsColumn ? columns : columns.filter((column) => column.key !== 'arguments');

  const onRenderCol = (item: CopilotApprovalOperationItem, column: IColumn): JSX.Element | null => {
    switch (column.key) {
      case 'status': {
        const state = getOutcomeState(item.outcome);

        return state ? <StateIcon state={state} size={StateIconSize.Medium} /> : <IPText>{'-'}</IPText>;
      }
      case 'message': {
        const entityText = getEntityText(item);

        if (!item.message && !entityText) {
          return <IPText block>{'-'}</IPText>;
        }

        if (item.message) {
          return <IPText block>{item.message}</IPText>;
        }

        return <IPText block>{entityText}</IPText>;
      }
      case 'arguments':
        if (!item.arguments) {
          return <IPText block>{'-'}</IPText>;
        }

        return (
          <pre
            style={{ fontFamily: 'Consolas, monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {item.toolName}&nbsp;
            {item.arguments}
          </pre>
        );
      default:
        return null;
    }
  };

  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('copilot:Panel.ApprovalDetails.Title')}
      height={600}
      width={700}
      maxWidth={'min(90vw, 700px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={400}
      hideFooter={false}
      optionalButtons={
        !showArgumentsColumn ? (
          <IPLink onClick={() => setShowArgumentsColumn(true)}>{t('copilot:Panel.ApprovalDetails.ShowDetails')}</IPLink>
        ) : undefined
      }
    >
      <IPStack verticalFill>
        <ScrollableStackItem>
          <BaseTableList
            compact
            items={items}
            columns={visibleColumns}
            selectionMode={IPSelectionMode.none}
            onRenderCol={onRenderCol}
            setColumns={setColumns}
            disableSort={true}
          />
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};
